import { Response } from "superagent";

import config from "config/config";
import { ExternalUserPayload } from "helpers/WizardHelper";
import { UserForm } from "models/admin/admin";
import { ExternalUser } from "models/customers/customers";
import { apiRequest, baseApiRequest } from "./apiInterceptor";
import { getTabToken } from "common/MetaActions";

export class UsersAPI {
  static async searchExternalUsers(emailPrefix: string, entraTenantId?: string): Promise<Response> {
    return new Promise((resolve, reject) => {
      apiRequest
        .get(config.api.user.find)
        .set("Accept", "application/json")
        .set("Authorization", "Bearer " + getTabToken())
        .query({ find: emailPrefix, entraTenantId: entraTenantId })
        .end((err, res) => {
          err ? reject(err) : resolve(res);
        });
    });
  }
  static async adminSearchExternalUsers(emailPrefix: string): Promise<Response> {
    /// Searches all external users in the system by email prefix, regardless of entra tenant
    return new Promise((resolve, reject) => {
      apiRequest
        .get(config.api.user.adminFind)
        .set("Accept", "application/json")
        .set("Authorization", "Bearer " + getTabToken())
        .query({ find: emailPrefix })
        .end((err, res) => {
          err ? reject(err) : resolve(res);
        });
    });
  }

  static async searchInternalUsers(query: any): Promise<Response> {
    return new Promise((resolve, reject) => {
      apiRequest
        .get(config.api.user.internalUsers)
        .query({ find: query })
        .end((err, res) => {
          err ? reject(err) : resolve(res);
        });
    });
  }

  static async editExternalUser(user: UserForm) {
    return new Promise((resolve, reject) => {
      apiRequest
        .patch(config.api.user.adminUser.editExternal)
        .send(user)
        .end((err, res) => {
          err ? reject({ err, res }) : resolve(res);
        });
    });
  }

  static async sendActivation(email: string) {
    return new Promise((resolve, reject) => {
      baseApiRequest
        .post(config.api.user.resetPassword)
        .set("Accept", "application/json")
        .send({
          email: email
        })
        .end((err, res) => {
          err
            ? reject({
                err,
                res
              })
            : resolve(res);
        });
    });
  }

  static async fetchInternalUserLogin(code: string | null) {
    const response = await baseApiRequest
      .post(config.api.user.internalUserLogin)
      .set("Accept", "application/json")
      .send({
        AzureAdRedirectUrl:
          window.location.origin + "/authenticate-internal-user",
        AzureCode: code
      });
    return response.body;
  }

  static async fetchInternalUserLoginEntraAccessToken(accessToken: string) {
    if (!accessToken) {
      throw new Error("accessToken is required");
    }

    const response = await apiRequest
      .post(config.api.user.internalUserLoginWithEntraToken)
      .set("Accept", "application/json")
      .set("Content-Type", "application/json")
      .send(JSON.stringify(accessToken));
    return response.body;
  }

  static async fetchExternalUserLoginEntraAccessToken(accessToken: string) {
    if (!accessToken) {
      throw new Error("accessToken is required");
    }

    const response = await apiRequest
      .post(config.api.user.externalUserLoginWithEntraAccessToken)
      .set("Accept", "application/json")
      .set("Content-Type", "application/json")
      .send(JSON.stringify(accessToken));
    return response.body;
  }

  static async fetchUserLogin({
    code,
    password,
    username
  }: {
    code: string;
    password: string;
    username: string;
  }) {
    const response = await baseApiRequest
      .post(config.api.user.login)
      .set("Accept", "application/json")
      .send({
        AzureAdRedirectUrl:
          window.location.origin + "/authenticate-internal-user",
        AzureCode: code,
        UserId: username,
        Password: password
      });
    return response.body;
  }

  static async validateToken(token: string) {
    const response = await baseApiRequest.get(
      config.api.user.validateToken(token)
    );
    const validToken: string = response.body;
    return validToken;
  }

  static async setPasswordAndGetAuthFromKey(key: string, password: string) {
    const response = await baseApiRequest
      .put(config.api.user.activateUser)
      .set("Accept", "application/json")
      .send({ key, password });
    return response.body;
  }

  static async generateOnetimeLogin(email: string) {
    return await baseApiRequest
      .post(config.api.user.generateOnetimeLogin)
      .send({
        UserEmail: email
      });
  }

  static async onetimeLogin(onetimeLoginGuid: string) {
    const response = await baseApiRequest
      .post(config.api.user.onetimeLogin)
      .set("Accept", "application/json")
      .send({
        OnetimeLoginGuid: onetimeLoginGuid
      });
    return response.body;
  }

  static async validateResetPasswordToken(token: string) {
    return await baseApiRequest.get(config.api.user.isTokenValid(token));
  }

  static async sendSetNewPassword(password: string, key: string) {
    const response = await baseApiRequest
      .put(config.api.user.setPassword)
      .set("Accept", "application/json")
      .send({ password, key });
    return response.body;
  }

  static async sendResetPassword(email: string) {
    return await apiRequest.post(config.api.user.resetPassword).send({
      email: email
    });
  }

  static async saveCustomerExternalUserSettings(
    id: string,
    externalUsers: ExternalUserPayload[]
  ) {
    return await apiRequest.put(config.api.user.externalUsers.put()).send({
      CustomerId: id,
      ExternalUsers: externalUsers
    });
  }

  static async addNewExternalUserToCustomer(
    customerId: string,
    user: Partial<ExternalUser>
  ) {
    const response = await apiRequest
      .post(config.api.user.addNewExternalUserToCustomer(customerId))
      .send({
        ...user
      });
    return response.body;
  }
}
